import { environment } from "../config/environment";
import { fetchWithAuthRetry, AuthRequiredError } from "./authenticated-fetch";
import type { TimeFinderRequestDto, TimeFinderRequestResponse } from "@hai/common";
import type { CreateEventRequest, CreateEventResponse } from "./types";

/**
 * Deprecated: Use AuthRequiredError from authenticated-fetch instead.
 * Kept for backward compatibility.
 */
export class NotAuthenticatedError extends Error {
  constructor() {
    super("Authentication required");
  }
}

export class ApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string = environment.apiBaseUrl) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    };

    try {
      const response = await fetchWithAuthRetry(`${this.baseUrl}/${path}`, {
        ...init,
        headers,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API ${response.status}: ${text}`);
      }

      return (await response.json()) as T;
    } catch (err) {
      // Convert AuthRequiredError to NotAuthenticatedError for backward compatibility
      if (err instanceof AuthRequiredError) {
        throw new NotAuthenticatedError();
      }
      throw err;
    }
  }

  submitTimeFinder(body: TimeFinderRequestDto): Promise<TimeFinderRequestResponse> {
    return this.request<TimeFinderRequestResponse>("time/find", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  /**
   * Create a calendar event
   * @param body Event creation request
   * @returns Event details including HTML link
   */
  createEvent(body: CreateEventRequest): Promise<CreateEventResponse> {
    return this.request<CreateEventResponse>("time/create", {
      method: "POST",
      body: JSON.stringify(body),
    });
  }
}
